/**
 * Catálogo de eventos de analítica (plan de tracking como código) de wcar.co.
 *
 * Esta es la capa que usa el resto de la web: `trackEvent('nombre', { ... })`.
 * El mapa `AnalyticsEvents` define el nombre EXACTO de cada evento y la forma de
 * sus propiedades, así que TypeScript rechaza nombres inventados, propiedades de
 * más y tipos equivocados.
 *
 * Reglas del plan (no negociables — Mixpanel distingue mayúsculas):
 * - Nombres de evento: `objeto_verbo` en `snake_case` y en pasado
 *   (`cita_agendada`, no `AgendarCita`).
 * - Nombres de propiedad: `snake_case`, sin abreviar.
 * - Valores de texto: minúsculas y consistentes (`"toyota"`, no `"Toyota"`).
 * - Números SIN comillas: `precio: 45000000`, nunca `"$45.000.000"`.
 *   Un número mandado como string no se puede sumar ni promediar en Mixpanel.
 * - Propiedad que no aplica: se omite (`undefined`), nunca `null` ni `""`.
 * - NUNCA construir nombres de evento en runtime: `track('paso_' + n)` genera
 *   miles de nombres distintos y vuelve inservible el proyecto. Usa una propiedad.
 *
 * ⚠️ PII: el usuario de wcar.co ES el cliente. Ninguna propiedad puede llevar
 * nombre, correo, teléfono, cédula, placa ni dirección. Ver la nota de
 * privacidad en `mixpanel.ts`; hay un guardarraíl que las descarta, pero la
 * primera defensa es no mandarlas.
 *
 * Para agregar un evento: (1) añadir la entrada al mapa con su JSDoc, (2) llamar
 * `trackEvent()` lo más cerca posible de la acción que lo dispara, (3) escribir
 * su descripción en Lexicon (Mixpanel → Data Management → Lexicon).
 */
import { mpStartTimer, mpTrack, type EventProps } from "./mixpanel";

/**
 * Pantallas con evento de vista propia. NO está toda la web: solo las que son
 * denominador de algún embudo o métrica de adopción. Las páginas legales, de
 * políticas y las landings de campaña se quedan fuera a propósito — su tráfico
 * ya lo mide GA4 y aquí solo aportarían ruido.
 */
export type PaginaApp =
    | "home"
    | "catalogo"
    | "vehiculo_detalle"
    | "vender_tu_carro"
    | "cotizar"
    | "agendar_cita"
    | "separar_vehiculo"
    | "financiacion"
    | "contacto"
    | "nuestras_sedes"
    | "blog"
    | "blog_articulo"
    | "iniciar_sesion"
    | "registro"
    | "perfil"
    | "favoritos"
    | "gracias";

/**
 * Resuelve la pantalla a partir de la ruta. Se usa donde el evento nace en un
 * componente compartido (el widget de contacto vive en el Layout, así que puede
 * dispararse desde cualquier página) y no hay forma de pasarle la pantalla por
 * props sin tocar las treinta y tantas rutas.
 *
 * Las páginas no mapeadas caen en `home` a propósito: es preferible un valor
 * conocido a una propiedad ausente que rompería los desgloses.
 */
export const paginaDesdeRuta = (pathname: string): PaginaApp => {
    const ruta = pathname.toLowerCase();
    if (ruta === "/" || ruta === "") return "home";
    // El detalle tiene 3-4 segmentos; el listado, como mucho 2.
    if (ruta.startsWith("/compra-tu-carro") || ruta.startsWith("/compra-tu-moto") || ruta.startsWith("/compra-tu-camion") || ruta.startsWith("/compra-tu-van")) {
        return ruta.split("/").filter(Boolean).length > 2 ? "vehiculo_detalle" : "catalogo";
    }
    if (ruta.startsWith("/vende-tu-carro") || ruta.startsWith("/vende-tu-auto")) return "vender_tu_carro";
    if (ruta.startsWith("/cotizar")) return "cotizar";
    if (ruta.includes("/cita")) return "agendar_cita";
    if (ruta.startsWith("/carro/separar") || ruta.startsWith("/pago-exitoso")) return "separar_vehiculo";
    if (ruta.startsWith("/financiacion")) return "financiacion";
    if (ruta.startsWith("/contacto") || ruta.startsWith("/contacta-a-la-empresa")) return "contacto";
    if (ruta.startsWith("/nuestras-sedes")) return "nuestras_sedes";
    if (ruta.startsWith("/blog/")) return "blog_articulo";
    if (ruta.startsWith("/blog")) return "blog";
    if (ruta.startsWith("/iniciar-sesion")) return "iniciar_sesion";
    if (ruta.startsWith("/registrarse")) return "registro";
    if (ruta.startsWith("/perfil") || ruta.startsWith("/editar-usuario") || ruta.startsWith("/tus-vehiculos")) return "perfil";
    if (ruta.startsWith("/thankyou")) return "gracias";
    return "home";
};

/** Catálogos de inventario. Comparten página y filtros, cambia el tipo de activo. */
export type TipoCatalogo = "carros" | "indrive" | "motos" | "camiones" | "vans" | "mis_vehiculos";

/** Criterio de ordenamiento del catálogo. Coincide con `orderingOptions`. */
export type OrdenCatalogo = "relevancia" | "precio_desc" | "precio_asc" | "garantia";

/**
 * De dónde salió el clic a una ficha de vehículo. Es lo que permite comparar la
 * eficacia de cada superficie: el catálogo contra los relacionados contra el
 * home. Sin esto, todas las vistas de ficha se ven iguales.
 */
/** Superficie desde la que se tocó el corazón. */
export type OrigenFavorito = "tarjeta_vehiculo" | "detalle_vehiculo" | "favoritos";

/** Metadata del vehículo favorito. Sin placa ni datos del usuario (ver nota de PII arriba). */
export interface FavoritoProps {
    vehiculo_id: string;
    origen: OrigenFavorito;
    marca?: string;
    referencia?: string;
    anio?: number;
    precio?: number;
    tipo_vehiculo?: string;
}

export type OrigenVehiculo = "catalogo" | "relacionados" | "home" | "favoritos" | "enlace_directo" | "mis_vehiculos";

/** Tipo de cita. `separar` implica pago; `ver` y `vender` son leads. */
export type TipoCita = "ver" | "separar" | "vender";

/** Canales por los que el cliente puede compartir o contactar. */
export type CanalCompartir = "whatsapp" | "facebook" | "twitter" | "email" | "copiar_enlace" | "nativo";
export type CanalContacto = "formulario" | "whatsapp" | "videollamada";

/**
 * Secciones expandibles de la ficha del vehículo. Mide qué información necesita
 * un comprador antes de decidir: si el 70% abre "siniestros", esa información
 * debería estar visible de entrada, no escondida tras un acordeón.
 */
export type SeccionVehiculo = "siniestros" | "garantia" | "ficha_tecnica" | "descripcion";

/**
 * Plan de tracking. Clave = nombre del evento; valor = forma de sus propiedades.
 *
 * Cada evento indica a qué pregunta de negocio responde:
 *   (A) ¿Qué vehículos busca la gente — y cuáles busca y NO tenemos?
 *   (B) ¿El catálogo convierte visitas en fichas vistas?
 *   (C) ¿Qué inventario genera interés real y cuál se queda quieto?
 *   (D) ¿Dónde se cae el embudo hasta el lead (cita) y la separación (pago)?
 *   (E) ¿Funciona el lado de venta — cuántos cotizan su carro con nosotros?
 *   (F) ¿Qué funciones se usan de verdad (y cuáles se pueden matar)?
 */
export interface AnalyticsEvents {
    // ── Sesión / cuenta ────────────────────────────────────────────────────────
    /** (F) Cuenta creada. Se envía DESPUÉS de `identify`, o quedaría anónimo. */
    registro_completado: { metodo: "email" | "google" };
    /** (F) Sesión iniciada. Se envía DESPUÉS de `identify`. */
    sesion_iniciada: { metodo: "email" | "google" };
    /**
     * (F) Intento de inicio de sesión rechazado. NO lleva el correo: en wcar.co
     * el usuario es el cliente, así que el correo es dato personal incluso
     * cuando falla el login (a diferencia del proyecto interno de compras).
     */
    sesion_inicio_fallido: { metodo: "email" | "google"; motivo_error: string };
    /** (F) Se mostró el modal de login (al pulsar una tarjeta de vehículo o el aviso del detalle abierto por URL). Denominador del muro. */
    acceso_requerido_mostrado: { vehiculo_id?: string; origen: "tarjeta_vehiculo" | "detalle_url" | "favorito" };
    /** (F) El usuario cerró el modal de login sin entrar. */
    acceso_requerido_abandonado: { vehiculo_id?: string; origen: "tarjeta_vehiculo" | "detalle_url" | "favorito" };
    /** (F) El registro con email falló. Sin correo, por la misma razón que `sesion_inicio_fallido`. */
    registro_fallido: { motivo_error: string };
    /** (F) Se pidió el código para recuperar la contraseña. */
    recuperacion_clave_solicitada: Record<string, never>;
    /** (F) Se cambió la contraseña con el código. */
    recuperacion_clave_completada: Record<string, never>;
    /** (F) Falló un paso de la recuperación de contraseña. */
    recuperacion_clave_fallida: { paso: "olvide" | "codigo" | "nueva_clave"; motivo_error: string };
    /**
     * (C) El usuario guardó un vehículo en favoritos. Con el usuario identificado da la lista de
     * vehículos que guarda cada cliente; la metadata del activo evita cruzar con el backend.
     */
    favorito_agregado: FavoritoProps;
    /** (C) El usuario quitó un vehículo de favoritos. Contra `favorito_agregado` mide el arrepentimiento. */
    favorito_eliminado: FavoritoProps;
    /** (C) Falló guardar o quitar un favorito (el corazón se revierte). */
    favorito_fallido: { vehiculo_id: string; accion: "agregar" | "eliminar"; motivo_error: string };
    /** (C) Se abrió la lista de favoritos. `cantidad` = vehículos activos mostrados. */
    favoritos_vistos: { cantidad: number };
    /** (F) Cierre de sesión. Se envía ANTES de `reset()`, o se pierde la atribución. */
    sesion_cerrada: Record<string, never>;

    // ── Navegación ─────────────────────────────────────────────────────────────
    /**
     * (B)(F) Una pantalla se montó. Es el DENOMINADOR de todas las tasas de
     * conversión. NO es el auto-pageview del SDK (sigue apagado): se emite
     * manualmente con `useTrackOnce`, así que no hay ruido de rutas intermedias.
     */
    pagina_vista: { pagina: PaginaApp; tipo_catalogo?: TipoCatalogo };

    // ── Catálogo y búsqueda ────────────────────────────────────────────────────
    /**
     * (A)(B) **EL EVENTO MÁS IMPORTANTE DEL SITIO.** Se emitió una búsqueda en
     * el catálogo y llegaron resultados. Es la foto completa de la demanda:
     * qué filtra la gente, en qué rango de precio compra y qué NO encuentra.
     *
     * `resultados_count` en 0 es la métrica de oro: cada búsqueda sin resultados
     * es demanda que el inventario no está cubriendo. No hay un evento aparte
     * para eso — se obtiene filtrando este por `resultados_count == 0`, para no
     * contar dos veces la misma búsqueda.
     *
     * `termino_busqueda` viaja normalizado y recortado, y se DESCARTA si parece
     * una placa (ver `limpiarTerminoBusqueda`): el texto libre dice qué marcas y
     * modelos pide el mercado, pero una placa es dato de un vehículo real.
     *
     * `filtros` lleva las CLAVES de los filtros activos como lista
     * (`["marca", "precio"]`), lo que permite medir qué filtros importan sin
     * depender de sus valores.
     */
    catalogo_buscado: {
        tipo_catalogo: TipoCatalogo;
        termino_busqueda?: string;
        resultados_count: number;
        pagina: number;
        filtros_activos: number;
        filtros: string[];
        orden: OrdenCatalogo;
        /** Filtros de alto valor comercial, desglosados para poder agregarlos. */
        marca?: string;
        tipos_vehiculo?: string[];
        precio_min?: number;
        precio_max?: number;
        km_max?: number;
        anios?: string[];
        sedes_count?: number;
    };
    /** (A) Falló la consulta del catálogo. Un catálogo caído se ve idéntico a un catálogo vacío sin este evento. */
    catalogo_carga_fallida: { tipo_catalogo: TipoCatalogo; mensaje_error: string };
    /** (F) Cambio del criterio de ordenamiento. Dice si la gente compra por precio o por garantía. */
    catalogo_ordenado: { tipo_catalogo: TipoCatalogo; orden: OrdenCatalogo };
    /**
     * (B) Cambio de página del catálogo. `pagina` alta con pocas fichas abiertas
     * es señal de que el orden por defecto no está mostrando lo relevante primero.
     */
    catalogo_paginado: { tipo_catalogo: TipoCatalogo; pagina: number };
    /**
     * (B) Se limpiaron todos los filtros. Un pico aquí después de filtrar es
     * fricción: el usuario se arrinconó en una búsqueda sin resultados y volvió a empezar.
     */
    catalogo_filtros_limpiados: { tipo_catalogo: TipoCatalogo; filtros_activos: number };
    /**
     * (B)(C) Se abrió una ficha desde un listado. Contra `catalogo_buscado` da el
     * CTR del catálogo, y `posicion` dice si el orden está funcionando: si los
     * clics se concentran en la posición 10, lo relevante está enterrado.
     */
    vehiculo_card_abierto: {
        vehiculo_id: string;
        origen: OrigenVehiculo;
        posicion: number;
        pagina: number;
        marca?: string;
        precio?: number;
    };

    // ── Ficha del vehículo ─────────────────────────────────────────────────────
    /**
     * (C) **La métrica central de inventario**: se vio la ficha de un vehículo.
     * Lleva toda la metadata del activo para poder responder, sin cruzar con el
     * backend, qué marcas / rangos de precio / sedes generan interés y cuáles no
     * se mueven. Es también el denominador de todo lo que pasa dentro de la ficha.
     */
    vehiculo_visto: {
        vehiculo_id: string;
        origen: OrigenVehiculo;
        marca?: string;
        referencia?: string;
        anio?: number;
        precio?: number;
        kilometraje?: number;
        tipo_vehiculo?: string;
        transmision?: string;
        combustible?: string;
        sede?: string;
        tiene_garantia: boolean;
        tipo_garantia?: string;
        tiene_siniestros: boolean;
        tiene_descuento: boolean;
        /** Etiqueta comercial (preventa, fuera de estándar, reservado…). */
        etiqueta?: string;
    };
    /**
     * (C)(F) Se abrió una sección desplegable de la ficha. Dice qué información
     * necesita un comprador para decidir — y por lo tanto qué debería estar
     * visible sin un clic de por medio.
     */
    vehiculo_seccion_abierta: { vehiculo_id: string; seccion: SeccionVehiculo };
    /** (C) Se amplió una foto del vehículo. Señal de intención alta: el comprador está inspeccionando. */
    vehiculo_foto_ampliada: { vehiculo_id: string };
    /** Se abrió el video de YouTube del vehículo desde el botón de play del detalle. */
    vehiculo_video_abierto: { vehiculo_id: string };
    /**
     * (D) Se pidió ver el peritaje. `requirio_datos` marca si al usuario se le
     * pidió antes el formulario de contacto: esa comparación dice cuánta gente
     * abandona en el muro de datos y cuánta lo pasa.
     */
    vehiculo_peritaje_solicitado: { vehiculo_id: string; requirio_datos: boolean };
    /**
     * (D) El usuario entregó sus datos en el muro previo al peritaje. Es un
     * micro-lead: el NUMERADOR del muro (`vehiculo_peritaje_solicitado` con
     * `requirio_datos: true` es el denominador). Sin datos personales: solo el
     * vehículo por el que los entregó.
     */
    vehiculo_datos_entregados: { vehiculo_id: string };
    /** (C) Se compartió la ficha de un vehículo. Compartir es la señal más barata de deseo real. */
    vehiculo_compartido: { vehiculo_id?: string; canal: CanalCompartir };

    // ── Financiación ───────────────────────────────────────────────────────────
    /**
     * (D) Clic en un producto de financiación. En un ecommerce de usados la
     * financiación es el mayor desbloqueador de conversión: medir su demanda
     * desde la ficha es lo que justifica (o no) integrarla al checkout.
     */
    financiacion_solicitada: { origen: "ficha_vehiculo" | "catalogo" | "home" | "pagina_financiacion"; producto: string; vehiculo_id?: string };

    // ── Conversión: cita y separación ──────────────────────────────────────────
    /**
     * (D) Se abrió el flujo de separación del vehículo (el paso al pago).
     * `origen` compara el botón principal contra la barra fija de móvil y contra
     * la entrada por enlace con `?checkout`.
     */
    separacion_iniciada: {
        vehiculo_id: string;
        precio?: number;
        origen: "boton_principal" | "sticky_movil" | "enlace_checkout";
    };
    /**
     * (D) Se entró al formulario de cita. Arranca el cronómetro de `cita_agendada`
     * (ver `startCitaTimer`) y es el DENOMINADOR del embudo de agendamiento.
     */
    cita_iniciada: { tipo_cita: TipoCita; vehiculo_id?: string };
    /**
     * (D) **VALUE MOMENT** — la cita quedó agendada: es el lead que recibe el
     * equipo comercial. Lleva `$duration` automático (cronómetro arrancado en
     * `cita_iniciada`), así que se puede ver cuánta fricción tiene el formulario.
     *
     * `dias_anticipacion` es el hueco entre hoy y la fecha elegida: correlaciona
     * con la tasa de asistencia, y es lo que dice si hay que abrir más cupos
     * cercanos.
     */
    cita_agendada: {
        tipo_cita: TipoCita;
        vehiculo_id?: string;
        precio?: number;
        marca?: string;
        dias_anticipacion?: number;
    };
    /** (D) El agendamiento falló contra el backend. Marca la caída del embudo que NO es abandono. */
    cita_fallida: { tipo_cita: TipoCita; vehiculo_id?: string; mensaje_error: string };
    /**
     * (D) Se pidió el código de verificación por correo antes de confirmar la
     * cita. Entre este evento y `cita_agendada` está el paso de OTP, que es el
     * sospechoso número uno de abandono al final del embudo.
     */
    cita_otp_solicitado: { tipo_cita: TipoCita; vehiculo_id?: string };

    // ── Contacto ───────────────────────────────────────────────────────────────
    /** (D) Se abrió el widget "Contacta un asesor". Denominador de los canales de contacto. */
    contacto_asesor_abierto: { pagina: PaginaApp };
    /** (D) Se eligió un canal para hablar con un asesor. Dice por dónde quiere hablar el cliente. */
    contacto_canal_elegido: { canal: CanalContacto; pagina: PaginaApp };

    // ── Vender tu carro (adquisición de inventario) ────────────────────────────
    /** (E) Se entró al formulario de cotización. Arranca el cronómetro de `cotizacion_enviada`. */
    cotizacion_iniciada: Record<string, never>;
    /**
     * (E) Se completó un paso del formulario de cotización. Marca exactamente en
     * qué paso se cae la gente: el formulario pide datos personales ANTES de los
     * datos del carro, que es justo donde se suele perder al vendedor.
     */
    cotizacion_paso_completado: { paso: "datos_personales" | "datos_carro" | "envio"; paso_indice: number };
    /**
     * (E) **VALUE MOMENT** — se envió la cotización: inventario potencial que
     * entra al pipeline de compras. Lleva `$duration` automático.
     *
     * Solo metadata del vehículo (marca, año, kilometraje, ciudad). El nombre,
     * el correo y el teléfono del vendedor NO viajan.
     */
    cotizacion_enviada: {
        marca?: string;
        anio?: number;
        kilometraje?: number;
        ciudad?: string;
        departamento?: string;
    };
    /** (E) La cotización falló contra el backend. */
    cotizacion_fallida: { mensaje_error: string };
}

export type AnalyticsEventName = keyof AnalyticsEvents;

/**
 * Envía un evento del catálogo. Es no-op si Mixpanel no está configurado, así
 * que puede llamarse sin condicionar por "¿está activa la analítica?".
 *
 * No devuelve promesa ni lanza: la analítica nunca debe romper un flujo.
 *
 * `{ inmediato: true }` para los eventos que se disparan justo antes de salir de
 * la página (recarga, `window.open`, redirección a la pasarela): evita que el
 * evento se quede en la cola de envío por lotes y se pierda.
 */
export function trackEvent<K extends AnalyticsEventName>(
    name: K,
    props: AnalyticsEvents[K],
    opciones?: { inmediato?: boolean },
): void {
    mpTrack(name, props as EventProps, opciones?.inmediato);
}

/**
 * Arranca el cronómetro del agendamiento. Se llama al entrar al formulario de
 * cita; `cita_agendada` sale entonces con `$duration` = cuánto tardó el cliente
 * en completarlo. Un `$duration` alto en móvil es el argumento para simplificar
 * el formulario.
 */
export function startCitaTimer(): void {
    mpStartTimer("cita_agendada");
}

/** Arranca el cronómetro de la cotización (ver `startCitaTimer`). */
export function startCotizacionTimer(): void {
    mpStartTimer("cotizacion_enviada");
}
